import { useEffect, useState } from "react";
import { Loader2, LogOut, Save, Trash2 } from "lucide-react";

import { ErrorBanner, LoadingState, PageHeader } from "@/components/common/FeatureShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { api } from "@/lib/api";

/**
 * Screen composition — Settings (Account)
 *
 * Hero zone: form card sections (`max-w-3xl`, stacked `space-y-6`).
 * Scanning regions (compact): sidebar nav items, usage stat tiles.
 * Focus regions (airy): form fields (`grid gap-4`), card sections with
 *   generous internal padding.
 */
export default function AccountPage() {
  const { user, signOut } = useAuth();
  const { profile, profileLoading, updateProfile } = useUserProfile();
  const [displayName, setDisplayName] = useState(profile?.displayName ?? "");
  const [organisation, setOrganisation] = useState(profile?.organisation ?? "");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDisplayName(profile?.displayName ?? "");
    setOrganisation(profile?.organisation ?? "");
  }, [profile?.displayName, profile?.organisation]);

  if (profileLoading && !profile) {
    return <LoadingState label="Loading account" />;
  }

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateProfile({ displayName: displayName.trim() || null, organisation: organisation.trim() || null });
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Profile could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  const deleteAccount = async () => {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    setDeletingAccount(true);
    setError(null);
    try {
      await api.user.deleteAccount();
      await signOut();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "Account could not be deleted.");
      setDeletingAccount(false);
    }
  };

  return (
    <div className="min-h-screen">
      <PageHeader title="Account" description="Profile, plan details, and account access." />
      <main className="max-w-3xl space-y-6 p-4 md:p-6">
        <ErrorBanner message={error} />
        <section className="rounded-md border bg-card p-4">
          <h2 className="text-sm font-semibold">Profile</h2>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="space-y-2"><Label htmlFor="display-name">Display name</Label><Input id="display-name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} /></div>
            <div className="space-y-2"><Label htmlFor="organisation">Organisation</Label><Input id="organisation" value={organisation} onChange={(event) => setOrganisation(event.target.value)} /></div>
            <div className="space-y-2 md:col-span-2"><Label htmlFor="email">Email</Label><Input id="email" value={user?.email ?? ""} readOnly /></div>
          </div>
          <Button type="button" className="mt-4" onClick={() => void save()} disabled={saving}>{saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}Save profile</Button>
        </section>
        <section className="rounded-md border bg-card p-4">
          <h2 className="text-sm font-semibold">Usage</h2>
          <div className="mt-3 grid gap-3 text-sm md:grid-cols-3">
            <p className="rounded-md bg-muted p-3"><span className="block text-xs text-muted-foreground">Tier</span>{profile?.tier ?? "Unknown"}</p>
            <p className="rounded-md bg-muted p-3"><span className="block text-xs text-muted-foreground">Credits remaining</span>{profile?.creditsRemaining?.toLocaleString() ?? "0"}</p>
            <p className="rounded-md bg-muted p-3"><span className="block text-xs text-muted-foreground">Credits used</span>{profile?.messageCreditsUsed?.toLocaleString() ?? "0"}</p>
          </div>
        </section>
        <section className="rounded-md border bg-card p-4">
          <h2 className="text-sm font-semibold">Session</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={() => void signOut()}><LogOut className="size-4" />Sign out</Button>
            <Button type="button" variant={confirmingDelete ? "destructive" : "outline"} onClick={() => void deleteAccount()} disabled={deletingAccount}>
              {deletingAccount ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
              {confirmingDelete ? "Confirm delete account" : "Delete account"}
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
}
